import {
  WbNoData,
  getOnHoverScrollbarStyle,
  useElementSize,
} from '@agilelab/plugin-wb-platform';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react';
import { NodeProps, useUpdateNodeInternals } from 'reactflow';
import { DataSourceEmptyStateIcon } from './icons/DataSourcesEmptyState';

import { PrototypeCard } from '../../../components/PrototypeCard';
import { PrototypeSection } from '../../../components/PrototypeSection';
import { usePrototypeInformationGraphContext } from '../../../PrototypeInformationGraphContext';
import { groupByDomain } from '../../../utils';
import { DataSource as DataSourceComponent } from './components/DataSource';

export type DataSourcesData = {
  style: CSSProperties;
};
export const DATA_SOURCES_NODE_ID = 'data_sources';

const SCROLL_BAR_WIDTH = 13;

export type DataSourcesProps = NodeProps<DataSourcesData>;

const useStyles = makeStyles(
  theme => ({
    scroll: {
      height: '100%',
      width: '100%',
      overflowY: 'scroll',
      direction: 'rtl',
      '& > *': {
        direction: 'ltr',
      },
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      ...getOnHoverScrollbarStyle(theme, false),
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    actionsContainer: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItem: 'center',
    },
    aiIcon: {
      color: theme.palette.accent.main,
    },
    header: {
      paddingRight: theme.spacing(1),
      paddingLeft: SCROLL_BAR_WIDTH,
    },
    card: {
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(3) - SCROLL_BAR_WIDTH,
    },
    emptyState: {
      width: '100%',
      maxWidth: '125px',
    },
  }),
  { name: 'DataSources' },
);

export const DataSourcesContent = ({ id }: DataSourcesProps) => {
  const classes = useStyles();

  const {
    prototype: { dataSources },
  } = usePrototypeInformationGraphContext();
  const updateNodeInternals = useUpdateNodeInternals();

  const onScroll = useCallback(() => {
    updateNodeInternals(id);
  }, [updateNodeInternals, id]);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { clientHeight } = useElementSize(contentRef, 30);

  useEffect(() => {
    updateNodeInternals(id);
  }, [updateNodeInternals, clientHeight, id, dataSources]);

  const dataSourcesByDomain = useMemo(
    () => groupByDomain(dataSources ?? []),
    [dataSources],
  );

  return (
    <div
      ref={scrollRef}
      className={clsx(classes.scroll, 'nowheel', 'nodrag')}
      onScroll={onScroll}
    >
      <div ref={contentRef} className={classes.content}>
        {Object.entries(dataSourcesByDomain).map(([domain, items]) => (
          <DataSourceComponent
            parentRef={scrollRef}
            key={domain}
            name={domain}
            dataSources={items}
          />
        ))}
      </div>
    </div>
  );
};

export const DataSources = (props: DataSourcesProps) => {
  const {
    prototype: { dataSources },
  } = usePrototypeInformationGraphContext();
  const classes = useStyles();

  const emptyState = (
    <PrototypeCard style={{ height: '100%' }} className={classes.card}>
      <WbNoData
        fillContainer
        text="Integrate data from multiple sources to create your prototype"
        icon={<DataSourceEmptyStateIcon className={classes.emptyState} />}
      />
    </PrototypeCard>
  );

  return (
    <>
      <PrototypeSection
        title="Data Sources"
        className={clsx('nowheel', 'nodrag')}
        headerClassName={classes.header}
        style={{
          ...props.data.style,
          paddingRight: 0,
          paddingLeft: 24 - SCROLL_BAR_WIDTH,
        }}
      >
        {dataSources?.length ? <DataSourcesContent {...props} /> : emptyState}
      </PrototypeSection>
    </>
  );
};
